import { Link } from 'react-router-dom';
import { useCourtStats } from '@/api/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { GlobeSpinner } from '@/components/ui/globe-spinner';
import { PageBanner } from '@/components/layout/PageBanner';
import { Scale, ArrowRight, Gavel, Building2, Database } from 'lucide-react';

export function CourtDashboardPage() {
  const { data, isLoading } = useCourtStats();

  return (
    <div className="space-y-6 animate-fade-up p-1">
      <PageBanner>
        <div className="flex items-center justify-between">
          <div className="banner-chip">
            <h1 className="text-2xl font-bold banner-title">Court System Registry</h1>
          </div>
        </div>
        <div className="flex gap-4 flex-wrap">
          <div className="banner-chip">
            <Scale className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <span className="text-sm">{data?.totalCases.toLocaleString() ?? '—'} cases</span>
          </div>
          <div className="banner-chip">
            <Building2 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <span className="text-sm">{data?.totalCourts.toLocaleString() ?? '—'} courts</span>
          </div>
          <div className="banner-chip">
            <Database className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <span className="text-sm">{data?.totalSources.toLocaleString() ?? '—'} sources</span>
          </div>
        </div>
      </PageBanner>

      {isLoading ? (
        <GlobeSpinner label="Loading court stats..." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: animation + CTA */}
          <Card className="flex flex-col items-center justify-center p-6">
            <Scale className="h-24 w-24 text-amber-500/60 mb-6" />
            <Link to="/courts/cases" className="w-full">
              <Button className="w-full">
                Search Cases <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Card>

          {/* Middle column: case types breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Gavel className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                Cases by Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data?.casesByType.map(item => (
                  <div key={item.case_type} className="flex items-center justify-between">
                    <span className="text-sm capitalize">{item.case_type}</span>
                    <Badge variant="secondary">{item.count.toLocaleString()}</Badge>
                  </div>
                ))}
                {(!data?.casesByType || data.casesByType.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">No cases yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right column: recent cases */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Scale className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                Recent Cases
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data?.recentCases.map(c => (
                  <div key={c.id} className="border-b border-border pb-2 last:border-0">
                    <p className="text-sm font-medium truncate" title={c.case_name ?? ''}>
                      {c.case_name || c.case_number}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-[10px]">{c.case_type}</Badge>
                      <span className="text-xs text-muted-foreground">{c.court_code}</span>
                      {c.date_filed && (
                        <span className="text-xs text-muted-foreground">
                          {new Date(c.date_filed).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {(!data?.recentCases || data.recentCases.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">No cases yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default CourtDashboardPage;
